import * as React from 'react';
import { Layout, Spin, Menu } from 'antd';
import { resolve } from 'inversify-react';
import { RouteComponentProps, Switch as RouterSwitch } from 'react-router';
import { Link } from 'react-router-dom';
const SubMenu = Menu.SubMenu;
const { Sider, Content } = Layout;

import { ConfigurationService } from 'src/core/services/configuration.service';
import { IdentityService } from 'src/core/services/identity.service';
import { PrivateRoute } from 'src/router';
import { GeneralController } from './general/general.controller';
import { CrmController } from './crm/crm.controller';
import { ProductsController } from './products/products.controller';
import { BookingsController } from './bookings/bookings.controller';
import { AccountingController } from './accounting/accounting.controller';
import { HomeController } from './home/home.controller';
import { DocumentTemplateListController } from './reports/report-list.controller';
import SqlCommand from './home/sql-command';
import { ConfigOptionsService } from 'src/core/services/config-options.service';
import * as Permissions from './permissions';
import { CommunicationService } from 'src/core/services/communication.service';
import { ToastNotifications } from 'src/core/ui/toast';
import { RulesController } from './rules/rules.controller';
import { GDPRController } from 'src/app/gdpr/gdpr.controller'
import PingComponent from 'src/core/ui/ping-component';
import { ZuluIcon } from 'src/app/shared/icon';

interface RouteProps<T> extends RouteComponentProps<T> {
}

interface State {
	sidebarExpanded: boolean;
	isBusy: boolean;
	windowClasses: string[];
	version: string;
	versionDate: string;
	logo: string;
	openKeys?: string[];
}

export class MainView extends React.Component<RouteProps<any>, State> {
	rootSubmenuKeys = ['crm', 'bookings', 'products', 'accounting', 'adm', 'rules'];

	@resolve
	private identityService: IdentityService;

	@resolve
	private configService: ConfigurationService;

	@resolve
	private configOptionsService: ConfigOptionsService;

	@resolve
	private communicationService: CommunicationService;

	@resolve
	private toast: ToastNotifications;

	constructor(props: Readonly<RouteProps<any>>) {
		super(props);

		this.state = {
			sidebarExpanded: true,
			isBusy: false,
			windowClasses: ['window', 'window-normal'],
			version: '',
			versionDate: '',
			logo: ''
		};
	}

	componentDidMount(): void {
		this.configService.current()
			.then((appConfig) => {
				this.setState({
					version: appConfig.version,
					versionDate: appConfig.versionDate
				})
			});

		this.communicationService.EventEmitter.on('showInfo', this.toastNotification);
		this.getLogo();
	}

	componentWillUnmount(): void {
		this.communicationService.EventEmitter.removeListener('showInfo', this.toastNotification);
	}

	private async getLogo(logoNew?: string) {
		let logo = await this.configOptionsService.getLogo();
		this.setState({ logo: logo });
	}

	private isAuthorized(permissions: string[]): boolean {
		return permissions.some(m => this.identityService.isAuthorized(m));
	}

	handleMenuClick = (e): void => {
		switch (e.key) {
			case 'logoff':
				this.identityService.logOff();
				this.props.history.push('/login');
				break;
			case 'trigger':
				this.expandSidebar();
				break;
			default:
				this.props.history.push(e.key);
				break;
		};
	};

	expandSidebar = (): void => {
		const sidebarExpanded = !this.state.sidebarExpanded;
		this.setState({
			sidebarExpanded: sidebarExpanded
		});
	};

	handleOpenChange = (openKeys): void => {
		const latestOpenKey = this.state.openKeys != null ? openKeys.find(key => this.state.openKeys.indexOf(key) === -1) : null;
		if (latestOpenKey == null || this.rootSubmenuKeys.indexOf(latestOpenKey) === -1) {
			this.setState({ openKeys });
		} else {
			this.setState({
				openKeys: latestOpenKey ? [latestOpenKey] : [],
			});
		}
	};

	setBusy = (isBusy: boolean): void => {
		this.setState({ isBusy: isBusy });
	}


	private toastNotification = (title, message, type) => {
		this.toast.showToast(title, message, type, 7);
	}

	setLogo = (logoNew: string) => {
		this.setState({ logo: logoNew });
	};

	render(): React.ReactNode {
		const match = this.props.match;

		return (
			<Layout className="container">
				<Sider
					collapsible
					trigger={null}
					collapsed={this.state.sidebarExpanded}
					onCollapse={this.expandSidebar}
					width="250">
					<div className="logo">
						<Link to="/" style={{ height: "100%" }}>
							<img src={'data:image/png;base64,' + this.state.logo} style={{ height: '100%' }} alt="LOGO" />
						</Link>
					</div>
					<Menu
						theme="dark"
						mode="inline"
						openKeys={this.state.openKeys}
						onOpenChange={this.handleOpenChange}
						onClick={this.handleMenuClick}>
						<Menu.Item
							key="trigger"
							onClick={this.expandSidebar}
						>
							<ZuluIcon
								className="trigger"
								type={this.state.sidebarExpanded ? 'menu-unfold' : 'menu-fold'}
								onClick={this.expandSidebar}
							/>
							<span>
								<ul style={{ listStyle: 'none', display: 'inline-block', verticalAlign: 'middle' }}>
									<li style={{ lineHeight: '1.1em', fontSize: 'smaller' }}>
										<span><b>BOOKEM v{this.state.version} ({this.state.versionDate})</b></span>
									</li>
									<li style={{ lineHeight: '1.1em', fontSize: 'smaller' }}>
										<span style={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', overflow: 'hidden', width: '125px' }}>
											{this.identityService.username}
										</span>
									</li>
								</ul>
							</span>
						</Menu.Item>
						{
							this.isAuthorized(Permissions.profilePermission) && !this.isAuthorized(Permissions.userWritePermission) &&
							<Menu.Item key={`/general/users/${this.identityService.userId}`}>
								<ZuluIcon type="user" />
								<span>Profile</span>
							</Menu.Item>
						}
						{
							this.isAuthorized(Permissions.crmPermissions) &&
							<SubMenu key="crm" title={<span><ZuluIcon type="team" /> <span>CRM</span></span>}>
								{
									this.isAuthorized(Permissions.clientPermissions) &&
									<Menu.Item key="clients">
										<Link to="/crm/clients">Clients</Link>
									</Menu.Item>
								}
								{
									this.isAuthorized(Permissions.gdprPermissions) &&
									this.isAuthorized(Permissions.clientGDPRPermissions) &&
									<Menu.Item key="gdpr">
										<Link to="/crm/gdpr">GDPR</Link>
									</Menu.Item>
								}
							</SubMenu>
						}
						{
							this.isAuthorized(Permissions.bookingPermissions) &&
							<SubMenu key="bookings" title={<span><ZuluIcon type="shopping" /> <span>Bookings</span></span>}>
								{
									this.isAuthorized(Permissions.quotePermission) && <Menu.Item key="quotes">
										<Link to="/bookings/quotes">Quotes</Link>
									</Menu.Item>
								}
								{
									this.isAuthorized(Permissions.quoteGroupPermission) && <Menu.Item key="groups">
										<Link to="/bookings/groups">Groups</Link>
									</Menu.Item>
								}
								{
									this.isAuthorized(Permissions.quotePermission) && <Menu.Item key="confirmation-quotes">
										<Link to="/bookings/confirmation-quotes">Confirmation Quotes</Link>
									</Menu.Item>
								}
								{
									this.isAuthorized(Permissions.agentPermissions) &&
									<Menu.Item key="agents">
										<Link to="/bookings/agents">Agents</Link>
									</Menu.Item>
								}
								{
									this.isAuthorized(Permissions.policyPermissions) &&
									<Menu.Item key="policies">
										<Link to="/bookings/policies">Agent Policies</Link>
									</Menu.Item>
								}
								{
									this.isAuthorized(Permissions.policyPermissions) &&
									<Menu.Item key="quote-policies">
										<Link to="/bookings/quote-policies">Quote Policies</Link>
									</Menu.Item>
								}
								{
									this.isAuthorized(Permissions.policyPermissions) &&
									<Menu.Item key="quote-product-policies">
										<Link to="/bookings/quote-product-policies">Quote Product Policies</Link>
									</Menu.Item>
								}
								{
									this.isAuthorized(Permissions.policyPermissions) &&
									<Menu.Item key="sell-margin-policies">
										<Link to="/bookings/sell-margin-policies">Sell Margin Policies</Link>
									</Menu.Item>
								}
								{
									this.isAuthorized(Permissions.policyPermissions) &&
									<Menu.Item key="quote-product-visibility-policies">
										<Link to="/bookings/quote-product-visibility-policies">Quote Product Visibility Policies</Link>
									</Menu.Item>
								}
							</SubMenu>
						}
						{
							this.isAuthorized(Permissions.productsPermissions) &&
							<SubMenu key="products" title={<span><ZuluIcon type="shopping-cart" /> <span>Products</span></span>}>
								{
									this.isAuthorized(Permissions.productPermissions) &&
									<Menu.Item key="products">
										<Link to="/products/products">Products</Link>
									</Menu.Item>
								}
								{
									this.isAuthorized(Permissions.supplierPermissions) &&
									<Menu.Item key="suppliers">
										<Link to="/products/suppliers">Suppliers</Link>
									</Menu.Item>
								}
								{
									this.isAuthorized(Permissions.productCategoryPermissions) &&
									<Menu.Item key="categories">
										<Link to="/products/categories">Product Categories</Link>
									</Menu.Item>
								}
								{
									this.isAuthorized(Permissions.allotmentPermissions) &&
									<Menu.Item key="allotment">
										<Link to="/products/allotment">Allotments</Link>
									</Menu.Item>
								}
								{
									this.isAuthorized(Permissions.productsPermissions) &&
									<Menu.Item key="contractSimulator">
										<Link to="/products/contractSimulator">Contract Simulator</Link>
									</Menu.Item>
								}
								{
									this.isAuthorized(Permissions.productProviderPermissions) &&
									<Menu.Item key="product-providers">
										<Link to="/products/product-providers">Product Providers</Link>
									</Menu.Item>
								}
								{
									this.isAuthorized(Permissions.entityMappingPermission) &&
									<Menu.Item key="entity-mappings">
										<Link to="/products/entity-mappings">Mappings</Link>
									</Menu.Item>
								}
							</SubMenu>
						}
						{
							this.isAuthorized(Permissions.accountingPermissions) &&
							<SubMenu key="accounting" title={<span><ZuluIcon type="wallet" /> <span>Accounting</span></span>}>
								<SubMenu key="suppliers" title="Suppliers">
									{
										this.isAuthorized(Permissions.invoicePermission) &&
										<Menu.Item key="suppliers-costs">
											<Link to="/accounting/suppliers-costs">Costs</Link>
										</Menu.Item>
									}
									{
										this.isAuthorized(Permissions.paymentPermission) &&
										<Menu.Item key="suppliers-invoices">
											<Link to="/accounting/suppliers-invoices">Invoices</Link>
										</Menu.Item>
									}
									{
										this.isAuthorized(Permissions.paymentPermission) &&
										<Menu.Item key="suppliers-payments">
											<Link to="/accounting/suppliers-payments">Payments</Link>
										</Menu.Item>
									}
								</SubMenu>
								<SubMenu key="clients" title="Clients">
									{
										this.isAuthorized(Permissions.paymentPermission) &&
										<Menu.Item key="clients-invoices">
											<Link to="/accounting/clients-invoices">Invoices</Link>
										</Menu.Item>
									}
									{
										this.isAuthorized(Permissions.paymentPermission) &&
										<Menu.Item key="clients-payments">
											<Link to="/accounting/clients-payments">Payments</Link>
										</Menu.Item>
									}
								</SubMenu>
								<SubMenu key="agents" title="Agents">
									{
										this.isAuthorized(Permissions.paymentPermission) &&
										<Menu.Item key="agents-invoices">
											<Link to="/accounting/agents-invoices">Invoices</Link>
										</Menu.Item>
									}
									{
										this.isAuthorized(Permissions.paymentPermission) &&
										<Menu.Item key="agents-payments">
											<Link to="/accounting/agents-payments">Payments</Link>
										</Menu.Item>
									}
								</SubMenu>
							</SubMenu>
						}
						{
							this.isAuthorized(Permissions.userTasksPermissions) &&
							<SubMenu key="user-tasks" title={<span><ZuluIcon type="check-square-o" /> <span>User tasks</span></span>}>

								<Menu.Item key='usertasks'>
									<Link to='/general/usertasks'>User tasks</Link>
								</Menu.Item>
							</SubMenu>
						}
						{
							<Menu.Item key='reports'>
								<Link to='/reports'><span><ZuluIcon type="book" /> <span>Reports</span></span></Link>
							</Menu.Item>
						}
						{
							this.isAuthorized(Permissions.generalPermission) &&
							<SubMenu key="adm" title={<span><ZuluIcon type="setting" /> <span>Administration</span></span>}>
								{
									this.isAuthorized(Permissions.geographyPermission) &&
									<SubMenu key="adm-location" title={<span><ZuluIcon type="global" /> <span>Geography</span></span>}>
										{
											this.isAuthorized(Permissions.regionPermission) &&
											<Menu.Item key="regions">
												<Link to="/general/regions">Regions</Link>
											</Menu.Item>
										}
										{
											this.isAuthorized(Permissions.placePermission) &&
											<Menu.Item key="places">
												<Link to="/general/places">Places</Link>
											</Menu.Item>
										}
									</SubMenu>
								}

								{
									this.isAuthorized(Permissions.currenciesPermissions) &&
									<SubMenu key="adm-currency" title={<span><ZuluIcon type="pound" /> <span>Currency</span></span>}>
										{
											this.isAuthorized(Permissions.currencyPermission) &&
											<Menu.Item key="currencies">
												<Link to="/general/currencies">Currencies</Link>
											</Menu.Item>
										}
										{
											this.isAuthorized(Permissions.currencyExchangePermission) &&
											<Menu.Item key="exchanges">
												<Link to="/products/currencies/exchanges">Exchange rates</Link>
											</Menu.Item>
										}
										{
											this.isAuthorized(Permissions.marketPermission) &&
											<Menu.Item key="market">
												<Link to="/general/market">Markets</Link>
											</Menu.Item>
										}
										{
											this.isAuthorized(Permissions.taxPermission) &&
											<Menu.Item key="taxes">
												<Link to="/general/taxes">Taxes</Link>
											</Menu.Item>
										}
									</SubMenu>
								}
								{
									this.isAuthorized(Permissions.docsPermission) &&
									<SubMenu key="adm-document" title={<span><ZuluIcon type="file" /> <span>Documents & Reports</span></span>}>
										{
											this.isAuthorized(Permissions.languagePermission) &&
											<Menu.Item key="language">
												<Link to="/general/language">Languages</Link>
											</Menu.Item>
										}
										{
											this.isAuthorized(Permissions.brandPermission) &&
											<Menu.Item key="brands">
												<Link to="/general/brands">Brands</Link>
											</Menu.Item>
										}
										{
											this.isAuthorized(Permissions.docTemplateCategoryPermission) &&
											<Menu.Item key="document-template-categories">
												<Link to="/general/document-template-categories">Categories</Link>
											</Menu.Item>
										}
										{
											this.isAuthorized(Permissions.docTemplatePermission) &&
											<Menu.Item key="document-templates">
												<Link to="/general/document-templates">Templates</Link>
											</Menu.Item>
										}
										{
											this.isAuthorized(Permissions.emailTemplatePermission) &&
											<Menu.Item key="email-document-templates">
												<Link to="/general/email-document-templates">Email Templates</Link>
											</Menu.Item>
										}
									</SubMenu>
								}
								{
									this.isAuthorized(Permissions.userPermission) &&
									<SubMenu key="adm-security" title={<span><ZuluIcon type="safety" /> <span>Security</span></span>}>
										<Menu.Item key="users">
											<Link to="/general/users">Users</Link>
										</Menu.Item>
										<Menu.Item key="roles">
											<Link to="/general/roles">Roles & Permissions</Link>
										</Menu.Item>
										{
											this.isAuthorized(Permissions.apiKeyPermission) &&
											<Menu.Item key="api-keys">
												<Link to="/general/api-keys">API Keys</Link>
											</Menu.Item>
										}
									</SubMenu>
								}
								{
									this.isAuthorized(Permissions.extensibilityPermission) &&
									<SubMenu key="adm-entities" title={<span><ZuluIcon type="database" /> <span>Additional table fields</span></span>}>
										{
											this.isAuthorized(Permissions.taxonomyPermission) &&
											<Menu.Item key="taxonomies">
												<Link to="/general/taxonomies">Taxonomies</Link>
											</Menu.Item>
										}
										{
											this.isAuthorized(Permissions.schemaPermission) &&
											<Menu.Item key="entity-schemas">
												<Link to="/general/entity-schemas">Entity Schemas</Link>
											</Menu.Item>
										}
									</SubMenu>
								}

								{
									this.isAuthorized(Permissions.rulesPermission) &&
									<SubMenu key="adm-rules" title={<span><ZuluIcon type="robot" /> <span>Automation</span></span>}>
										{
											this.isAuthorized(Permissions.rulePermission) &&
											<Menu.Item key="rules">
												<Link to="/rules/rules">Rules</Link>
											</Menu.Item>
										}
										{
											this.isAuthorized(Permissions.rulePermission) &&
											<Menu.Item key='cache'>
												<Link to='/general/cache'>Cache store</Link>
											</Menu.Item>
										}
									</SubMenu>
								}
								{
									this.isAuthorized(Permissions.otherPermission) &&
									<SubMenu key="adm-other" title={<span><ZuluIcon type="tool" /> <span>Other</span></span>}>
										{
											this.isAuthorized(Permissions.airlinePermission) &&
											<Menu.Item key="airlines">
												<Link to="/general/airlines">Airlines</Link>
											</Menu.Item>
										}
										{
											this.isAuthorized(Permissions.configPermission) &&
											<Menu.Item key="config-option">
												<Link to="/general/config-option">System Configurations</Link>
											</Menu.Item>
										}
										{
											this.isAuthorized(Permissions.configPermission) &&
											<Menu.Item key="sql-command">
												<Link to="/sql-command">Run Sql</Link>
											</Menu.Item>
										}
									</SubMenu>
								}
							</SubMenu>
						}
						<Menu.Item key="logoff">
							<ZuluIcon type="logout" />
							<span>Log off</span>
						</Menu.Item>
					</Menu>
				</Sider>
				<Layout className="container">
					<Content style={{ overflowY: 'auto' }}>
						<Spin spinning={this.state.isBusy} size="large" className="mt-3" wrapperClassName="mt-3">
							<PingComponent />
							<RouterSwitch>
								<PrivateRoute
									exact
									path="/"
									setBusy={this.setBusy}
									component={HomeController}
								/>
								<PrivateRoute
									exact
									path="/reports"
									setBusy={this.setBusy}
									component={DocumentTemplateListController}
								/>
								<PrivateRoute
									exact
									path="/home"
									setBusy={this.setBusy}
									component={HomeController}
								/>
								<PrivateRoute
									path={`${match.url}sql-command`}
									setBusy={this.setBusy}
									component={SqlCommand}
								/>
								<PrivateRoute
									path={`${match.url}crm/gdpr`}
									setBusy={this.setBusy}
									component={GDPRController}
								/>
								<PrivateRoute
									path={`${match.url}crm`}
									setBusy={this.setBusy}
									component={CrmController}
								/>
								<PrivateRoute
									path={`${match.url}general`}
									setBusy={this.setBusy}
									component={GeneralController}
									setLogo={this.setLogo}
								/>
								<PrivateRoute
									path={`${match.url}products`}
									setBusy={this.setBusy}
									component={ProductsController}
								/>
								<PrivateRoute
									path={`${match.url}accounting`}
									setBusy={this.setBusy}
									component={AccountingController}
								/>
								<PrivateRoute
									path={`${match.url}bookings`}
									setBusy={this.setBusy}
									component={BookingsController}
								/>
								<PrivateRoute
									path={`${match.url}rules`}
									setBusy={this.setBusy}
									component={RulesController}
								/>
							</RouterSwitch>
						</Spin>
					</Content>
				</Layout>
			</Layout>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/main.view.tsx